import { useEffect, useRef, type RefObject } from 'react';
import type { MapRef } from 'react-map-gl';

const DEFAULT_VIEW = { longitude: -98, latitude: 31, zoom: 4 };
const FIT_PADDING = 40;

/** The view the coverage map opens on, before anything is fitted. */
export { DEFAULT_VIEW };

/** [minLng, minLat, maxLng, maxLat]. */
export type Box = [number, number, number, number];

/**
 * Fits the coverage map to what it draws, keeping clear of the legend over its bottom-left corner
 * (read at fit time: a legend that grows doesn't move the camera on its own), and flies back to
 * the default view with nothing shaped. Refits only when `fitKey` moves. Filling a tearsheet
 * (`fill`), the container's size settles after the map has measured it (the open animation, then
 * any window resize): the canvas is matched to it and refitted at once.
 *
 * @param {object} options - The map, the box and its key, the legend, the container and `fill`.
 */
export function useCoverageFit({
  mapRef,
  fitBox,
  fitKey,
  legendRef,
  containerRef,
  fill,
}: {
  mapRef: MapRef | null;
  fitBox: Box | null;
  fitKey: string;
  legendRef: RefObject<HTMLUListElement>;
  containerRef: RefObject<HTMLDivElement>;
  fill: boolean;
}) {
  const fit = (map: ReturnType<MapRef['getMap']>, duration: number) => {
    if (fitBox && fitBox.every(Number.isFinite)) {
      map.fitBounds(
        [
          [fitBox[0], fitBox[1]],
          [fitBox[2], fitBox[3]],
        ],
        {
          padding: {
            top: FIT_PADDING,
            right: FIT_PADDING,
            bottom: FIT_PADDING + (legendRef.current?.offsetHeight ?? 0),
            left: FIT_PADDING,
          },
          maxZoom: 12,
          duration,
        }
      );
    } else if (fitKey === 'bare') {
      map.flyTo({
        center: [DEFAULT_VIEW.longitude, DEFAULT_VIEW.latitude],
        zoom: DEFAULT_VIEW.zoom,
        duration,
      });
    }
  };
  const fitLatest = useRef(fit);
  fitLatest.current = fit;

  useEffect(() => {
    const map = mapRef?.getMap();
    if (map) fit(map, 500);
    // fitKey stands for fitBox: refit only when what is drawn moves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapRef, fitKey]);

  useEffect(() => {
    const map = mapRef?.getMap();
    const container = containerRef.current;
    if (!fill || !map || !container) return undefined;
    const observer = new ResizeObserver(() => {
      map.resize();
      fitLatest.current(map, 0);
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [fill, mapRef, containerRef]);
}
